import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { ApplicationFormData } from '../types/application';
import { BrandConfig, defaultBrand } from '../config/brands';
import BusinessInformation from './steps/BusinessInformation';
import ApplicantInformation from './steps/ApplicantInformation';
import ContactInformation from './steps/ContactInformation';
import OwnershipInformation from './steps/OwnershipInformation';
import DocumentUpload from './steps/DocumentUpload';
import ReviewAndSubmit from './steps/ReviewAndSubmit';
import ProgressBar from './ProgressBar';

const FormContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 10px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow-x: hidden;
  
  @media (min-width: 768px) {
    padding: 20px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    margin-bottom: 40px;
  }

  h1 {
    color: ${props => props.theme.primaryColor};
    font-size: 1.8rem;
    margin-bottom: 10px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s;

    @media (min-width: 768px) {
      font-size: 2.5rem;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  .subtitle {
    color: #666;
    font-size: 0.9rem;
    line-height: 1.5;

    @media (min-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const HeaderLogo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  img {
    height: 60px;
    max-width: 250px;
    object-fit: contain;

    @media (min-width: 768px) {
      height: 80px;
      max-width: 300px;
    }
  }
`;

const StepContainer = styled.div`
  min-height: 400px;
  padding: 10px 0;
  
  @media (min-width: 768px) {
    min-height: 600px;
    padding: 20px 0;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding: 15px 0;
  border-top: 1px solid #e0e0e0;
  gap: 10px;
  
  @media (min-width: 768px) {
    margin-top: 40px;
    padding: 20px 0;
    gap: 0;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 16px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 100px;
  flex: 1;
  max-width: 150px;
  
  @media (min-width: 768px) {
    padding: 12px 24px;
    font-size: 16px;
    min-width: 120px;
    flex: none;
    max-width: none;
  }
  
  ${props => props.variant === 'primary' ? `
    background: ${props.theme.primaryColor};
    color: white;

    &:hover:not(:disabled) {
      background: ${props.theme.primaryColor}dd;
    }
  ` : `
    background: #f5f5f5;
    color: #333;
    
    &:hover:not(:disabled) {
      background: #e0e0e0;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const steps = [
  { title: 'Business Information', component: BusinessInformation },
  { title: 'Applicant Information', component: ApplicantInformation },
  { title: 'Contact & Financial', component: ContactInformation },
  { title: 'Ownership Structure', component: OwnershipInformation },
  { title: 'Document Upload', component: DocumentUpload },
  { title: 'Review & Submit', component: ReviewAndSubmit }
];

const defaultFormData: ApplicationFormData = {
  // Business Information
  businessName: '',
  tradingNames: '',
  companyNumber: '',
  businessAddress: {
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  },
  businessType: '',
  businessDescription: '',
  businessModel: 'online',
  websiteOrApp: '',
  taxNumber: '',

  // Applicant Information
  applicantFirstName: '',
  applicantLastName: '',
  dateOfBirth: '',
  residentialAddress: {
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  },

  // Financial & Contact Information
  contactName: '',
  contactNumbers: '',
  paymentMobile: '',
  businessEmail: '',
  bankName: '',
  bankDetails: '',

  // Ownership & Control Structure
  majorOwners: '',
  ownershipPercentages: {
    owner1: '',
    owner2: '',
    owner3: ''
  },
  directors: '',

  // Multicurrency Banking
  multicurrencyBanking: true,

  // Terms Acceptance
  termsAccepted: {
    legalAuthority: false,
    documentProvision: false,
    policyAgreement: false
  }
};

interface MultiStepFormProps {
  onSubmit: (data: ApplicationFormData) => Promise<void>;
  isSubmitting?: boolean;
  brand?: BrandConfig;
}

const MultiStepForm: React.FC<MultiStepFormProps> = ({ onSubmit, isSubmitting = false, brand = defaultBrand }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<ApplicationFormData>(defaultFormData);
  const [isValid, setIsValid] = useState(false);

  const updateData = (data: Partial<ApplicationFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (isValid && currentStep === steps.length - 1) {
      await onSubmit(formData);
    }
  };

  const handleLogoClick = () => {
    const landingPath = brand.id === 'finaconnect' ? '/' : `/${brand.id}`;
    window.location.href = `https://finaconnect.com${landingPath}`;
  };

  const CurrentStepComponent = steps[currentStep].component;

  const theme = {
    primaryColor: brand.primaryColor,
    secondaryColor: brand.secondaryColor
  };

  return (
    <ThemeProvider theme={theme}>
      <FormContainer>
      <Header>
        {brand.id === 'tgpay' && brand.logo ? (
          <HeaderLogo onClick={handleLogoClick}>
            <img src={brand.logo} alt={brand.name} />
          </HeaderLogo>
        ) : (
          <h1 onClick={handleLogoClick}>{brand.name}</h1>
        )}
        <p className="subtitle">
          Merchant Onboarding Application
        </p>
      </Header>

      <ProgressBar 
        currentStep={currentStep} 
        totalSteps={steps.length}
        steps={steps.map(step => step.title)}
      />

      <StepContainer>
        <CurrentStepComponent
          data={formData}
          updateData={updateData}
          onNext={handleNext}
          onPrev={handlePrev}
          isFirst={currentStep === 0}
          isLast={currentStep === steps.length - 1}
          onValidationChange={setIsValid}
          brand={brand}
        />
      </StepContainer>

      <NavigationButtons>
        <div>
          {currentStep > 0 && (
            <Button variant="secondary" onClick={handlePrev}>
              Previous
            </Button>
          )}
        </div>
        
        <div>
          {currentStep < steps.length - 1 ? (
            <Button 
              variant="primary" 
              onClick={handleNext}
              disabled={!isValid}
            >
              Next Step
            </Button>
          ) : (
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </div>
      </NavigationButtons>
    </FormContainer>
    </ThemeProvider>
  );
};

export default MultiStepForm;